import { PIECES_BY_VALUE, PIECE_VALUES } from '../consts/pieces.js';
import { decode62, encode62 } from './base62.js';

function flattenBoard(board = []) {
  if (!Array.isArray(board) || board.length === 0) return [];
  if (Array.isArray(board[0])) {
    return board.flatMap((row) => row);
  }
  return board;
}

function encodeLegacyBoard(board) {
  return flattenBoard(board)
    .map(({ id, teamIndex = -1, value }) =>
      [encode62(id), teamIndex, value].join(',')
    )
    .join('?');
}

function decodeLegacyBoard(encodedBoard) {
  const allPieces = encodedBoard.split('?').map((encodedPiece) => {
    const [encodedId, teamIndex, value] = encodedPiece.split(',');
    return {
      id: decode62(encodedId),
      teamIndex: +teamIndex,
      value: +value,
    };
  });
  return Array(4)
    .fill()
    .map((_, y) =>
      Array(8)
        .fill()
        .map((_, x) => allPieces[y * 8 + x])
    );
}

function encodeLegacyGraveyard(graveyard = []) {
  return Array(2)
    .fill()
    .map((_, playerIndex) => {
      const counts = {};
      for (const piece of graveyard) {
        if (piece.teamIndex !== playerIndex) continue;
        counts[piece.value] = (counts[piece.value] || 0) + 1;
      }
      return Object.values(PIECE_VALUES)
        .sort((a, b) => a - b)
        .map((val) => counts[val] || 0)
        .join(',');
    })
    .join('?');
}

function decodeLegacyGraveyard(encodedGraveyard) {
  return encodedGraveyard.split('?').reduce((all, encodedPlayer, playerIndex) => [
    ...all,
    ...encodedPlayer.split(',').reduce((pieces, pieceCount, value) => [
      ...pieces,
      ...Array(+pieceCount).fill({
        ...PIECES_BY_VALUE[value],
        teamIndex: playerIndex,
      }),
    ], []),
  ], []);
}

function encodeLegacyFollowing(following = {}) {
  return Array(2)
    .fill()
    .map((_, playerIndex) => {
      const playerFollowing = following[playerIndex];
      if (!playerFollowing) return '';

      const { pieceId, following: followMap = {} } = playerFollowing;
      const pieceFollowingArray = Object.entries(followMap).map(
        ([otherPieceId, turns]) => [encode62(otherPieceId), turns].join(',')
      );
      return [pieceId, ...pieceFollowingArray].join('?');
    })
    .join('#');
}

function decodeLegacyFollowing(encodedFollowing) {
  return encodedFollowing.split('#').reduce((all, encodedFollowings, playerIndex) => {
    if (!encodedFollowings) return all;
    const [pieceId, ...encodedPieceFollowing] = encodedFollowings.split('?');
    return {
      ...all,
      [playerIndex]: {
        pieceId: +pieceId,
        following: encodedPieceFollowing.reduce((map, encodedOtherPiece) => {
          if (!encodedOtherPiece) return map;
          const [otherPieceId, turnsFollowing] = encodedOtherPiece.split(',');
          return {
            ...map,
            [decode62(otherPieceId)]: +turnsFollowing,
          };
        }, {}),
      },
    };
  }, {});
}

/** Original `::` delimited position string (for size comparison + old saves). */
export function encodeLegacyPosition({
  turnNumber = 0,
  board = [],
  graveyard = [],
  following = {},
  turnsWithoutAction = 0,
  lastActionPlayerIndex = 0,
} = {}) {
  return [
    encode62(turnNumber),
    encodeLegacyBoard(board),
    encodeLegacyGraveyard(graveyard),
    encodeLegacyFollowing(following),
    encode62(turnsWithoutAction),
    encode62(lastActionPlayerIndex),
  ].join('::');
}

export function decodeLegacyPosition(encodedGameState) {
  const [
    encodedTurnNumber,
    encodedBoard,
    encodedGraveyard,
    encodedFollowing,
    encodedTurnsWithoutAction,
    encodedLastActionPlayerIndex,
  ] = String(encodedGameState).split('::');

  return {
    turnNumber: decode62(encodedTurnNumber),
    board: decodeLegacyBoard(encodedBoard),
    graveyard: decodeLegacyGraveyard(encodedGraveyard),
    following: decodeLegacyFollowing(encodedFollowing ?? ''),
    turnsWithoutAction: decode62(encodedTurnsWithoutAction),
    lastActionPlayerIndex: decode62(encodedLastActionPlayerIndex),
  };
}
